import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

const PHASE_DURATIONS = [13600, 1400, 1400, 3400, 1000];

export const LogoAnimation = () => {
    const reduce = useReducedMotion();
    const [phase, setPhase] = useState(0);
    const [cycle, setCycle] = useState(0);

    useEffect(() => {
        if (reduce) return undefined;
        const timer = setTimeout(() => {
            setPhase((p) => {
                const next = (p + 1) % PHASE_DURATIONS.length;
                if (next === 0) setCycle((c) => c + 1);
                return next;
            });
        }, PHASE_DURATIONS[phase]);
        return () => clearTimeout(timer);
    }, [phase, reduce]);

    if (reduce) {
        return (
            <>
                <img
                    src="/assets/logo-full.png"
                    alt="Innovated Global IT Solutions logo — four-petal digital flower with the tagline Ideas. Innovation. Impact."
                    className="logo-ondark w-full h-auto"
                    data-testid="hero-logo-static"
                />
                <img
                    src="/assets/logo-full-dark.png"
                    alt=""
                    aria-hidden="true"
                    className="logo-onlight w-full h-auto"
                />
            </>
        );
    }

    const layersVisible = phase < 4;
    const showName = phase >= 1 && phase < 4;
    const showTagline = phase >= 2 && phase < 4;
    const showFull = phase === 3;

    return (
        <div
            className="relative w-full"
            style={{ aspectRatio: "1370 / 919" }}
            role="img"
            aria-label="Animated logo of Innovated Global IT Solutions: four petals revolving around a glowing blue center, followed by the company name and tagline"
            data-testid="hero-logo-animation"
        >
            <AnimatePresence>
                {layersVisible && (
                    <motion.div
                        key="layers"
                        className="absolute inset-0"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: showFull ? 0 : 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.7 }}
                    >
                        <motion.div
                            className="absolute left-1/2 top-0"
                            style={{ width: "49.8%", marginLeft: "-24.9%" }}
                            initial={{ opacity: 0, scale: 0.85 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                        >
                            <div className="relative">
                                <div
                                    className="orb-pulse absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                                    style={{
                                        width: "55%",
                                        height: "55%",
                                        background: "radial-gradient(circle, rgba(0,102,255,0.5) 0%, rgba(0,240,255,0.18) 45%, transparent 70%)",
                                        filter: "blur(18px)",
                                    }}
                                />
                                <motion.img
                                    key={cycle}
                                    src="/assets/flower-mark.png"
                                    alt=""
                                    aria-hidden="true"
                                    className="relative w-full h-auto"
                                    initial={{ rotate: 0 }}
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 11, delay: 0.3, ease: [0.45, 0, 0.35, 1] }}
                                />
                            </div>
                        </motion.div>

                        <motion.img
                            src="/assets/logo-name.png"
                            alt=""
                            aria-hidden="true"
                            className="logo-ondark absolute left-0 w-full"
                            style={{ top: "69%" }}
                            initial={false}
                            animate={
                                showName
                                    ? { opacity: 1, y: 0, filter: "brightness(1.6)" }
                                    : { opacity: 0, y: 18, filter: "brightness(1)" }
                            }
                            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                            onAnimationComplete={() => {
                                if (showName) {
                                    const el = document.getElementById("ig-logo-name");
                                    if (el) el.style.filter = "brightness(1)";
                                }
                            }}
                            id="ig-logo-name"
                        />
                        <motion.img
                            src="/assets/logo-name-dark.png"
                            alt=""
                            aria-hidden="true"
                            className="logo-onlight absolute left-0 w-full"
                            style={{ top: "69%" }}
                            initial={false}
                            animate={showName ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
                            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        />

                        <motion.div
                            aria-hidden="true"
                            className="tagline-flow absolute left-0 w-full"
                            style={{
                                top: "94.3%",
                                WebkitMaskImage: "url(/assets/logo-tagline.png)",
                                maskImage: "url(/assets/logo-tagline.png)",
                                WebkitMaskSize: "100% 100%",
                                maskSize: "100% 100%",
                                WebkitMaskRepeat: "no-repeat",
                                maskRepeat: "no-repeat",
                            }}
                            initial={false}
                            animate={showTagline ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
                            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.div
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-1/2 rounded-full"
                style={{
                    width: "72%",
                    height: "58%",
                    marginLeft: "-36%",
                    marginTop: "-29%",
                    background: "radial-gradient(circle, rgba(0,102,255,0.55) 0%, rgba(0,240,255,0.22) 45%, transparent 70%)",
                    filter: "blur(30px)",
                }}
                initial={false}
                animate={showFull ? { opacity: [0, 0.95, 0.5], scale: [0.85, 1.08, 1] } : { opacity: 0, scale: 0.85 }}
                transition={{ duration: 1.4, ease: "easeOut" }}
            />

            <motion.img
                src="/assets/logo-full.png"
                alt=""
                aria-hidden="true"
                className="logo-ondark absolute inset-0 w-full h-auto"
                initial={false}
                animate={{ opacity: showFull ? 1 : 0 }}
                transition={{ duration: 0.7 }}
            />
            <motion.img
                src="/assets/logo-full-dark.png"
                alt=""
                aria-hidden="true"
                className="logo-onlight absolute inset-0 w-full h-auto"
                initial={false}
                animate={{ opacity: showFull ? 1 : 0 }}
                transition={{ duration: 0.7 }}
            />
        </div>
    );
};
